import React, { FunctionComponent, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import colors from '../common/theme/colors';
import constants from '../common/theme/constants';
import KitIcon from './KitIcon';
import { TranslateFunc } from '../common/types';

export interface KitPaginationProps {
  boundaryCount?: number;
  count: number;
  page?: number;
  onChange?: (page: number) => void;
  disabled?: boolean;
  siblingCount?: number;
  t: TranslateFunc;
}

const StyledUl = styled.ul`
  font-family: ${({ theme }) => theme.font.fontFamily};
  font-weight: ${constants.fontWeight.normal};
  color: ${({ theme }) => theme.page.body.text};
  list-style: none;
  display: flex;
  justify-content: center;
  margin: 0;
  padding: 0;
`;

const StyledLi = styled.li`
  flex-basis: ${constants.spacing.xl}rem;
  justify-content: center;
  display: flex;
  margin: 0;
`;

const StyledButton = styled.button`
  border: none;
  background: none;
  line-height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme, disabled }) => (disabled ? colors.gray_20 : theme.page.body.text)};

  &.selected {
    font-weight: bold;
  }

  &:focus {
    outline: none;
    border-radius: 3px;
    box-shadow: 0 0 0 ${constants.border.width}px ${({ theme }) => theme.focus};
  }
`;

const ArrowButton = styled(StyledButton)`
  width: ${constants.spacing.absolute.l}px;
`;

const StyledEllipsis = styled.div<Pick<KitPaginationProps, 'disabled'>>`
  color: ${({ theme, disabled }) => (disabled ? colors.gray_20 : theme.page.body.text)};
  margin: 0 ${constants.spacing.sm}rem;
`;

const KitPagination: FunctionComponent<KitPaginationProps> = ({
  t, 
  count, 
  boundaryCount = 1,
  disabled = false,
  onChange,
  page = 1,
  siblingCount = 1,
} : KitPaginationProps) => {
  const themeContext = useContext(ThemeContext);

  const isDisabled: boolean = disabled;
  const isBackDisabled: boolean = disabled || page === 1;
  const isForwardDisabled: boolean = disabled || page === count;
  const [startEllipsis, endEllipsis, ellipsis] = ['start-ellipsis', 'end-ellipsis', '...'];
  const iconColor = themeContext.button.secondary.normal.text;

  // https://dev.to/namirsab/comment/2050
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(Math.max(count - boundaryCount + 1, boundaryCount + 1), count);
  const startNumber = boundaryCount + 1 < count - boundaryCount ? [boundaryCount + 1] : [];
  const endNumber = count - boundaryCount > boundaryCount ? [count - boundaryCount] : [];

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages.length > 0 ? endPages[0] - 2 : count - 1
  );

  const onSelect = (currentPage: number) => {
    if (onChange) {
      onChange(currentPage);
    }
  };

  const prev = () => {
    if (onChange) {
      onChange(page - 1);
    }
  };

  const next = () => {
    if (onChange) {
      onChange(page + 1);
    }
  };

  const itemList = [
    ...startPages,

    // Start ellipsis
    ...(siblingsStart > boundaryCount + 2 ? [startEllipsis] : startNumber),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    ...(siblingsEnd < count - boundaryCount - 1 ? [endEllipsis] : endNumber),

    ...endPages,
  ];

  return (
    <nav role='navigation' aria-label='pagination navigation'>
      <StyledUl>
        <StyledLi key='prev'>
          <ArrowButton
            aria-label={t('kit:pagination.go_to_previous')}
            type='button'
            onClick={prev}
            disabled={isBackDisabled}
          >
            <KitIcon
              fill={isBackDisabled ? colors.gray_20 : iconColor}
              icon='angle-left'
              size={`${constants.iconSize.s}rem`}
            />
          </ArrowButton>
        </StyledLi>
        {itemList.map((item) => (
          <StyledLi key={item}>
            {item === startEllipsis || item === endEllipsis ? (
              <StyledEllipsis>{ellipsis}</StyledEllipsis>
            ) : (
              <StyledButton
                disabled={isDisabled}
                type='button'
                onClick={() => onSelect(item)}
                aria-label={t(
                  `kit:pagination.${page === item ? 'current_page' : 'go_to_page'}`,
                  { page: item }
                )}
                aria-current={page === item ? 'true' : undefined}
                className={page === item ? 'selected' : undefined}
              >
                {item}
              </StyledButton>
            )}
          </StyledLi>
        ))}
        <StyledLi key='next'>
          <ArrowButton
            aria-label={t('kit:pagination.go_to_next')}
            type='button'
            onClick={next}
            disabled={isForwardDisabled}
          >
            <KitIcon
              fill={isForwardDisabled ? colors.gray_20 : iconColor}
              icon='angle-right'
              size={`${constants.iconSize.s}rem`}
            />
          </ArrowButton>
        </StyledLi>
      </StyledUl>
    </nav>
  );
};

export default KitPagination;
